import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';
import { formatDataForDataTable } from '../../utils/formatData';

const initialState = {
  loading: false,
  data: [],
  configuration: [],
  approvalData: [],
  taskdata: [],
  statusLoading: false,
  priorities: [],
  statusData: [],
  createdTasks:[],
  headAnalytics:[],
  analyticsLoading: false,
  deleteLoading: false,
  userAnalytics: [],
  userAnalyticsLoading: false,
  taskCategories: [],
  departmentEmployees: [],
  taskActivityLogs: [],
  taskComments: [],
  taskRating: [],
  assignableGoals: [],
  assignableGoalsLoading: false,
  linkedGoals: [],
  linkedGoalsLoading: false,
  subtasks: [],
  subtasksLoading: false,
  allTags: [],
  allTagsLoading: false,
};

export const getConfiguration = createAsyncThunk('taskManagement/getConfiguration', async (dept_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-configuration-data?dept_id=${dept_id}`,
  });
  return res;
});

export const createConfig = createAsyncThunk('taskManagement/createConfig', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/upsert-configuration-data`,
    data: data,
  });
  return res;
});

export const getApprovalTasks = createAsyncThunk('taskManagement/getApprovalTasks', async (date) => {
  const res = await apiRequest({
    method: 'get',
    url: `/task-management/get-pending-task-for-approval?date=${date}`,
  });
  return res;
});
export const approveRejectTask = createAsyncThunk('taskManagement/approveRejectTask', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `task-management/update-task-approval-status/${data.task_approval_id}`,
    data: data,
  });
  return res;
});

// Builds the query string for both task-list endpoints from a
// { status, from_date, to_date, tag_id } filters object. status defaults to
// "all"; from_date/to_date/tag_id are only included when actually set, so
// calling with no argument at all (a couple of call sites do this) cleanly
// means "show everything" instead of erroring like the old mandatory ?date=
// param did.
const buildTaskListQuery = (filters = {}) => {
  const { status = 'all', from_date, to_date, tag_id } = filters || {};
  const params = new URLSearchParams();
  params.set('status', status);
  if (from_date) params.set('from_date', from_date);
  if (to_date) params.set('to_date', to_date);
  if (tag_id) params.set('tag_id', tag_id);
  return params.toString();
};

// Cross-module tags - reuses the same registry PMS goals already use
// (/appraisal-tag-management). Read-only from here; tags are created/managed
// through the PMS Quarter/Tag management screens.
export const getAllTags = createAsyncThunk('taskManagement/getAllTags', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-tag-management?page=1&limit=no`,
  });
  return res;
});

export const getTasks = createAsyncThunk('taskManagement/getTasks', async (filters) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-all-target-department-tasks?${buildTaskListQuery(filters)}`,
  });
  return res;
});

export const createTask = createAsyncThunk('taskManagement/createTask', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/create-incoming-task`,
    data: data,
  });
  return res;
});

export const updateTask = createAsyncThunk('taskManagement/updateTask', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `task-management/update-task/${data.task_id}`,
    data: data,
  });
  return res;
});

export const updateTaskStatus = createAsyncThunk('taskManagement/updateTaskStatus', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/upsert-task-assignment`,
    data: data,
  });
  return res;
});

export const getTaskPriorities = createAsyncThunk('taskManagement/getTaskPriorities', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-task-priorities`,
  });
  return res;
});

export const getTaskStatuses = createAsyncThunk('taskManagement/getTaskStatuses', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-task-approval-status`,
  });
  return res;
});

export const getCreatedTasks = createAsyncThunk('taskManagement/getCreatedTasks', async (filters) =>{
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-all-source-department-tasks?${buildTaskListQuery(filters)}`,
  });
  return res;
})

export const getHeadAnalytics = createAsyncThunk('taskManagement/getHeadAnalytics', async ({ user_id, from_date, to_date } = {}) => {
  const params = new URLSearchParams();
  if (user_id) params.set('user_id', user_id);
  if (from_date) params.set('from_date', from_date);
  if (to_date) params.set('to_date', to_date);
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-head-analytics?${params.toString()}`,
  });
  return res;
});

export const deleteTask = createAsyncThunk('taskManagement/deleteTask', async (task_id) => {
  const res = await apiRequest({
    method: 'delete',
    url: `task-management/delete-task/${task_id}`,
  });
  return res;
}
);

export const getUserAnalytics = createAsyncThunk('taskManagement/getUserAnalytics', async ({ from_date, to_date } = {}) => {
  const params = new URLSearchParams();
  if (from_date) params.set('from_date', from_date);
  if (to_date) params.set('to_date', to_date);
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-user-analytics?${params.toString()}`,
  });
  return res;
});

export const getTaskCategories = createAsyncThunk('taskManagement/getTaskCategories', async (dept_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-task-categories?dept_id=${dept_id}`,
  });
  return res;
});

export const getDepartmentEmployees = createAsyncThunk('taskManagement/getDepartmentEmployees', async (dept_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-department-employees?department_id=${dept_id}`,
  });
  return res;
});

export const getTaksActivityLogs = createAsyncThunk('taskManagement/getTaksActivityLogs', async (task_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-task-activity/${task_id}`,
  });
  return res;
});

export const addComment = createAsyncThunk('taskManagement/addComment', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/add-task-comment/${data.task_id}`,
    data: data,
  });
  return res;
});

export const getComments = createAsyncThunk('taskManagement/getComments', async (task_id) => {
  const res = await apiRequest({
     url: `task-management/get-task-comments/${task_id}`,
  });
  return res;
});

export const updateComment = createAsyncThunk('taskManagement/updateComment', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `task-management/update-task-comment/${data.comment_id}`,
    data: data,
  });
  return res;
});

export const deleteComment = createAsyncThunk('taskManagement/deleteComment', async (comment_id) => {
  const res = await apiRequest({
    method: 'delete',
    url: `task-management/delete-task-comment/${comment_id}`,
  });
  return res;
});

export const AddtaskRating = createAsyncThunk('taskManagement/AddtaskRating', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/upsert-task-rating/${data.task_id}`,
    data: data,
  });
  return res;
});

export const getTaskRating = createAsyncThunk('taskManagement/getTaskRating', async (task_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/get-task-ratings/${task_id}`,
  });
  return res;
});

export const getAssignableGoals = createAsyncThunk('taskManagement/getAssignableGoals', async (user_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/assignable-goals?user_id=${user_id}`,
  });
  return res;
});

export const getTaskGoals = createAsyncThunk('taskManagement/getTaskGoals', async (task_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/tasks/${task_id}/goals`,
  });
  return res;
});

export const getSubtasks = createAsyncThunk('taskManagement/getSubtasks', async (task_id) => {
  const res = await apiRequest({
    method: 'get',
    url: `task-management/tasks/${task_id}/subtasks`,
  });
  return res;
});

export const createSubtasks = createAsyncThunk('taskManagement/createSubtasks', async ({ task_id, subtasks }) => {
  const res = await apiRequest({
    method: 'post',
    url: `task-management/tasks/${task_id}/subtasks`,
    data: { subtasks },
  });
  return res;
});

export const updateSubtask = createAsyncThunk('taskManagement/updateSubtask', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `task-management/subtasks/${data.subtask_id}`,
    data: data,
  });
  return res;
});

export const toggleSubtaskStatus = createAsyncThunk('taskManagement/toggleSubtaskStatus', async ({ subtask_id, is_completed }) => {
  const res = await apiRequest({
    method: 'patch',
    url: `task-management/subtasks/${subtask_id}/status`,
    data: { is_completed },
  });
  return res;
});

export const deleteSubtask = createAsyncThunk('taskManagement/deleteSubtask', async (subtask_id) => {
  const res = await apiRequest({
    method: 'delete',
    url: `task-management/subtasks/${subtask_id}`,
  });
  return res;
});

const taskManagementSlice = createSlice({
  name: 'taskManagement',
  initialState,
  reducers: {
    clearTaskComments: (state) => {
      state.taskComments = [];
    },
    clearTaskActivityLogs: (state) => {
      state.taskActivityLogs = [];
    },
    clearTaskRating: (state) => {
      state.taskRating = [];
    },
    clearSubtasks: (state) => {
      state.subtasks = [];
    },
  },
  extraReducers: (builder) => {
    //.................getConfiguration ................
    builder.addCase(getConfiguration.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getConfiguration.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        configuration: formatDataForDataTable(action.payload.data),
      };
    });
    // -----------------createConfig----------------------------
    builder.addCase(createConfig.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(createConfig.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------getTasks----------------------------
    builder.addCase(getTasks.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getTasks.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taskdata: action.payload.data,
      };
    });
    // -----------------createTask----------------------------
    builder.addCase(createTask.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(createTask.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------updateTask----------------------------
    builder.addCase(updateTask.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(updateTask.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------updateTaskStatus----------------------------
    builder.addCase(updateTaskStatus.pending, (state, action) => {
      return {
        ...state,
        statusLoading: true,
      };
    });
    builder.addCase(updateTaskStatus.fulfilled, (state, action) => {
      return {
        ...state,
        statusLoading: false,
      };
    });
    // -----------------getTaskPriorities----------------------------
    builder.addCase(getTaskPriorities.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getTaskPriorities.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        priorities: action.payload.data,
      };
    });
    // ------------------getApprovalTasks--------------------------------
    builder.addCase(getApprovalTasks.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getApprovalTasks.fulfilled, (state, action) => {
      return {
        ...state,
        approvalData: formatDataForDataTable(action.payload.data),
        loading: false,
      };
    });
    // ----------------approveRejectTask------------------------
    builder.addCase(approveRejectTask.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(approveRejectTask.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ---------------------getTaskStatuses--------------------------------
    builder.addCase(getTaskStatuses.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getTaskStatuses.fulfilled, (state, action) => {
      return {
        ...state,
        statusData: action.payload.data,
        loading: false,
      };
    });
    // -------------------------- GET CREATED TASK --------------------------------------
      builder.addCase(getCreatedTasks.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });

    builder.addCase(getCreatedTasks.fulfilled, (state, action) => {
      return {
        ...state,
        createdTasks: action.payload.data,
        loading: false,
      };
    });
    // --------------------- getHeadAnalytics--------------------------------
    builder.addCase(getHeadAnalytics.pending, (state, action) => {
      return {
        ...state,
        analyticsLoading: true,
      };
    });
    builder.addCase(getHeadAnalytics.fulfilled, (state, action) => {
      return {
        ...state,
        headAnalytics: action.payload.data,
        analyticsLoading: false,
      };
    });
      // --------------------- deleteTask--------------------------------
    builder.addCase(deleteTask.pending, (state, action) => {
      return {
        ...state,
        deleteLoading: true,
      };
    });
    builder.addCase(deleteTask.fulfilled, (state, action) => {
      return {
        ...state,
        deleteLoading: false,
      };
    });
      // --------------------- getUserAnalytics--------------------------------
    builder.addCase(getUserAnalytics.pending, (state, action) => {
      return {
        ...state,
        userAnalyticsLoading: true,
      };
    });
    builder.addCase(getUserAnalytics.fulfilled, (state, action) => {
      return {
        ...state,
        userAnalytics: action.payload.data,
        userAnalyticsLoading: false,
      };
    });
        // --------------------- getTaskCategories--------------------------------
    builder.addCase(getTaskCategories.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getTaskCategories.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taskCategories: action.payload.data,
      };
    });
      // --------------------- getDepartmentEmployees--------------------------------
    builder.addCase(getDepartmentEmployees.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getDepartmentEmployees.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        departmentEmployees: action.payload.data,
      };
    });
    // ------------------------- getTaksActivityLogs-------------------------------
    builder.addCase(getTaksActivityLogs.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getTaksActivityLogs.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taskActivityLogs: action.payload.data,
      };
    });
    // -------------------------- addComment-------------------------------
    builder.addCase(addComment.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(addComment.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -------------------------- getComments-------------------------------
    builder.addCase(getComments.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getComments.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taskComments: action.payload.data,
      };
    });
    // ---------------------------- updateComment-------------------------------
    builder.addCase(updateComment.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(updateComment.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ---------------------------- deleteComment-------------------------------
    builder.addCase(deleteComment.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(deleteComment.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------------------- AddtaskRating-------------------------------
    builder.addCase(AddtaskRating.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(AddtaskRating.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------------------- getTaskRating-------------------------------
    builder.addCase(getTaskRating.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getTaskRating.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taskRating: action.payload.data,
      };
    });
    // ----------------------------- getAssignableGoals-------------------------------
    builder.addCase(getAssignableGoals.pending, (state, action) => {
      return {
        ...state,
        assignableGoalsLoading: true,
      };
    });
    builder.addCase(getAssignableGoals.fulfilled, (state, action) => {
      return {
        ...state,
        assignableGoalsLoading: false,
        assignableGoals: action.payload.data,
      };
    });
    builder.addCase(getAssignableGoals.rejected, (state, action) => {
      return {
        ...state,
        assignableGoalsLoading: false,
      };
    });
    // ----------------------------- getAllTags-------------------------------
    builder.addCase(getAllTags.pending, (state, action) => {
      return {
        ...state,
        allTagsLoading: true,
      };
    });
    builder.addCase(getAllTags.fulfilled, (state, action) => {
      return {
        ...state,
        allTagsLoading: false,
        allTags: action.payload.data,
      };
    });
    builder.addCase(getAllTags.rejected, (state, action) => {
      return {
        ...state,
        allTagsLoading: false,
      };
    });
    // ----------------------------- getTaskGoals-------------------------------
    builder.addCase(getTaskGoals.pending, (state, action) => {
      return {
        ...state,
        linkedGoalsLoading: true,
      };
    });
    builder.addCase(getTaskGoals.fulfilled, (state, action) => {
      return {
        ...state,
        linkedGoalsLoading: false,
        linkedGoals: action.payload.data,
      };
    });
    builder.addCase(getTaskGoals.rejected, (state, action) => {
      return {
        ...state,
        linkedGoalsLoading: false,
      };
    });
    // ----------------------------- getSubtasks-------------------------------
    builder.addCase(getSubtasks.pending, (state, action) => {
      return {
        ...state,
        subtasksLoading: true,
      };
    });
    builder.addCase(getSubtasks.fulfilled, (state, action) => {
      return {
        ...state,
        subtasksLoading: false,
        subtasks: action.payload.data,
      };
    });
    builder.addCase(getSubtasks.rejected, (state, action) => {
      return {
        ...state,
        subtasksLoading: false,
      };
    });
    // ----------------------------- createSubtasks-------------------------------
    // Appends the newly-created rows straight from the response instead of
    // re-fetching the whole list - one network call instead of two, and no
    // flash/flicker of the existing items while they reload.
    builder.addCase(createSubtasks.pending, (state, action) => {
      return {
        ...state,
        subtasksLoading: true,
      };
    });
    builder.addCase(createSubtasks.fulfilled, (state, action) => {
      const ids = action.payload?.data?.subtask_ids || [];
      const submitted = action.meta.arg?.subtasks || [];
      const newRows = submitted
        .map((s, i) => ({
          subtask_id: ids[i],
          task_id: action.meta.arg.task_id,
          title: s.title,
          description: s.description || null,
          due_date: s.due_date || null,
          is_completed: 0,
          completed_at: null,
          created_at: new Date().toISOString(),
        }))
        .filter((row) => row.subtask_id !== undefined);
      return {
        ...state,
        subtasksLoading: false,
        subtasks: [...state.subtasks, ...newRows],
      };
    });
    builder.addCase(createSubtasks.rejected, (state, action) => {
      return {
        ...state,
        subtasksLoading: false,
      };
    });
    // ----------------------------- updateSubtask-------------------------------
    // Patches the edited row in place from the request itself - the API only
    // returns a success flag, not the row, so the request args are the source
    // of truth for what changed.
    builder.addCase(updateSubtask.fulfilled, (state, action) => {
      const { subtask_id, title, due_date } = action.meta.arg;
      return {
        ...state,
        subtasks: state.subtasks.map((s) =>
          s.subtask_id === subtask_id ? { ...s, title, due_date: due_date || null } : s
        ),
      };
    });
    // ----------------------------- toggleSubtaskStatus-------------------------------
    // True optimistic update: flips the checkbox the instant it's clicked
    // (on .pending, before the server even responds), and only rolls it back
    // if the request actually fails. This is the highest-frequency action in
    // this UI, so it's the one most worth making feel instant.
    builder.addCase(toggleSubtaskStatus.pending, (state, action) => {
      const { subtask_id, is_completed } = action.meta.arg;
      return {
        ...state,
        subtasks: state.subtasks.map((s) =>
          s.subtask_id === subtask_id
            ? {
                ...s,
                _prevCompleted: s.is_completed,
                is_completed: is_completed ? 1 : 0,
                completed_at: is_completed ? new Date().toISOString() : null,
              }
            : s
        ),
      };
    });
    builder.addCase(toggleSubtaskStatus.rejected, (state, action) => {
      const { subtask_id } = action.meta.arg;
      return {
        ...state,
        subtasks: state.subtasks.map((s) =>
          s.subtask_id === subtask_id && s._prevCompleted !== undefined
            ? { ...s, is_completed: s._prevCompleted, _prevCompleted: undefined }
            : s
        ),
      };
    });
    // ----------------------------- deleteSubtask-------------------------------
    // Optimistically removes the row immediately; if the request fails the
    // component falls back to a full re-fetch to resync (see TaskSubtasks.jsx).
    builder.addCase(deleteSubtask.pending, (state, action) => {
      const subtask_id = action.meta.arg;
      return {
        ...state,
        subtasks: state.subtasks.filter((s) => s.subtask_id !== subtask_id),
      };
    });
  },
});

export const { clearTaskComments, clearTaskActivityLogs, clearTaskRating, clearSubtasks } = taskManagementSlice.actions;

export default taskManagementSlice.reducer;
